<template>
  <div class="flex flex-1 relative">
    <button
      v-if="showFollowButton"
      @click="enableFollowing"
      class="absolute top-4 right-4 z-20 bg-white/90 text-gray-800 font-medium px-2 py-2 rounded-full shadow-lg border border-gray-200 flex items-center gap-2"
    >
      <img :src="GpsIcon" class="w-8 h-8 text-blue-600" />
    </button>
    <div v-if="!isMapInitialized" class="flex flex-1 w-full h-[calc(100vh-80px)] z-10">
      <div class="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
        <div v-if="!mapError" class="text-center p-4">
          <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-500 mx-auto mb-4"></div>
          <p class="text-lg font-medium text-gray-700">Kaart laden..</p>
        </div>
        <div
          v-else
          class="max-w-md mx-auto p-4 text-center"
        >
          <p class="text-lg font-semibold mb-2">
            {{ mapErrorMessage }}
          </p>
          <p class="text-sm text-gray-600 mb-4">
            Je kunt de punten nog steeds bekijken in de lijstweergave.
          </p>
        </div>
      </div>
    </div>
    <div 
      ref="mapContainer" 
      class="flex flex-1 w-full h-[calc(100vh-80px)] z-10"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { point, lineString, nearestPointOnLine, distance } from '@turf/turf'
import api from '../services/api'
import GpsIcon from '/icons/compass_icon.svg'

// Define marker icons
const markerIcons = {
  locked: '/static/markers/locked.svg',
  visited: '/static/markers/visited.svg',
  answer_correct: '/static/markers/answer_correct.svg',
  answer_wrong: '/static/markers/answer_wrong.svg',
  default: '/static/markers/default.svg'
}

export default {
  name: 'OutdoorMap',
  props: {
    currentLocation: {
      type: Object,
      default: null
    },
    points: {
      type: Object,
      required: true
    },
    onItemClick: {
      type: Function,
      required: true
    },
    isPointGPSLocked: {
      type: Function,
      default: () => false
    }
  },
  setup(props) {
    const store = useStore()
    const mapContainer = ref(null)
    const map = ref(null)
    const userMarker = ref(null)
    const pointMarkers = ref([])
    const isMapInitialized = ref(false)
    const isValidLocation = ref(false)
    const selectedPoint = ref(null)
    const isFollowingUser = ref(true)
    const isOpenPreviewOnClick = ref(false) // Controls click behaviour when clicking the marker // TODO make this controllable via settings or in combination with routing
    const mapError = ref(null) // string code for why the map failed to load
    
    // Refs for tracking state
    const lastRouteCalculationRef = ref(Date.now())
    const previousLocationRef = ref(null)
    const lastViewUpdateRef = ref(Date.now())
    const smoothedBearing = ref(0)
    const lastValidBearingRef = ref(null)
    const bearingBufferRef = ref([])
    const lastAnimationTimeRef = ref(0)
    const MIN_ANIMATION_INTERVAL = 300
    const pointStates = ref({}) // Track current point states
    const interactionHandlers = []
    const followTimeoutId = ref(null)
    const followTimeoutEnd = ref(null)
    const countdownNow = ref(Date.now())
    const countdownIntervalId = ref(null)

    // Computed properties from store
    const tourSettings = computed(() => store.getters['settings/tourSettings'])
    const activeRoute = computed(() => store.getters['navigation/activeRoute'])
    const routeInstructions = computed(() => store.getters['navigation/routeInstructions'])
    const calculatingRoute = computed(() => store.getters['navigation/calculatingRoute'])
    const showFollowButton = computed(() => isMapInitialized.value && !isFollowingUser.value)

    const mapErrorMessage = computed(() => {
      if (!mapError.value) return ''

      if (mapError.value === 'no_gps_points') {
        return 'De kaart kan niet worden geladen omdat er geen punten met een geldige GPS-locatie beschikbaar zijn.'
      }

      if (mapError.value === 'webgl_unsupported') {
        return 'De kaart kan niet worden geladen omdat je toestel geen (of een uitgeschakelde) WebGL-ondersteuning heeft.'
      }

      return 'De kaart kan niet worden geladen door een technische fout.'
    })

    // Helper function to get GPS position
    const getGPSPosition = (gpsLocation) => {
      if (!gpsLocation) return { lat: 0, lng: 0 }
      const [lat, lng] = gpsLocation.split(',').map(parseFloat)
      return { lat, lng }
    }

    // Helper function to safely get point state
    const getPointState = (pointId) => {
      return pointStates.value[pointId] || 'default'
    }

    // Helper to get a custom icon URL for a given point/state
    const getCustomIconForState = (point, state) => {
      const data = point?.data
      if (!data) return null

      if (state === 'locked') {
        //return data.icon_locked?.preview_url || null
        return data.icon_locked?.preview_url || data.icon?.preview_url || null
      }

      // default (and any other) state: only use the default icon
      return data.icon?.preview_url || null
    }

    // Helper to get the effective marker icon URL (custom or fallback)
    const getMarkerIconUrl = (point, state) => {
      const customIcon = getCustomIconForState(point, state)
      if (customIcon) return customIcon

      const iconKey = markerIcons[state] ? state : 'default'
      return markerIcons[iconKey]
    }

    // Check if current location is valid
    const checkLocationValidity = () => {
      if (props.currentLocation && 
          props.currentLocation.lat !== undefined && 
          props.currentLocation.lng !== undefined && 
          (props.currentLocation.lat !== 0 || props.currentLocation.lng !== 0)) {
        isValidLocation.value = true
      } else {
        isValidLocation.value = false
      }
    }

    const stopCountdownInterval = () => {
      if (countdownIntervalId.value) {
        clearInterval(countdownIntervalId.value)
        countdownIntervalId.value = null
      }
    }

    const startCountdownInterval = () => {
      if (countdownIntervalId.value) return
      countdownIntervalId.value = setInterval(() => {
        countdownNow.value = Date.now()
        if (!followTimeoutEnd.value) {
          stopCountdownInterval()
        }
      }, 200)
    }

    const clearFollowTimeout = () => {
      if (followTimeoutId.value) {
        clearTimeout(followTimeoutId.value)
        followTimeoutId.value = null
      }
      followTimeoutEnd.value = null
      stopCountdownInterval()
    }

    const scheduleAutoFollow = () => {
      clearFollowTimeout()
      followTimeoutEnd.value = Date.now() + 10000
      followTimeoutId.value = setTimeout(() => {
        isFollowingUser.value = true
        followTimeoutId.value = null
        followTimeoutEnd.value = null
        stopCountdownInterval()
        updateMapView()
      }, 10000)
      startCountdownInterval()
    }

    const enableFollowing = () => {
      if (!isFollowingUser.value) {
        isFollowingUser.value = true
        clearFollowTimeout()
        updateMapView()
      }
    }

    const disableFollowing = () => {
      if (isFollowingUser.value) {
        isFollowingUser.value = false
      }
      scheduleAutoFollow()
    }

    const attachInteractionListeners = () => {
      if (!map.value) return
      const events = ['dragstart', 'zoomstart', 'rotatestart', 'pitchstart']
      events.forEach(evt => {
        const handler = (event) => {
          if (event?.originalEvent) {
            disableFollowing()
          }
        }
        map.value.on(evt, handler)
        interactionHandlers.push({ evt, handler })
      })
    }

    const detachInteractionListeners = () => {
      if (!map.value) return
      while (interactionHandlers.length) {
        const { evt, handler } = interactionHandlers.pop()
        map.value.off(evt, handler)
      }
    }

    // Initialize map
    const initializeMap = () => {
      if (map.value) return // Map already initialized

      if (!props.points || Object.keys(props.points).length === 0) {
        // No points passed in – parent already handles this case, but keep for safety
        mapError.value = 'no_gps_points'
        return
      }

      // Find the first point that has a valid GPS location
      const firstPointWithGps = Object.values(props.points).find(p => {
        const gps = p?.data?.gps_location
        if (!gps) return false
        const [lat, lng] = gps.split(',').map(parseFloat)
        return !isNaN(lat) && !isNaN(lng)
      })

      if (!firstPointWithGps) {
        mapError.value = 'no_gps_points'
        return
      }

      // Check if WebGL / MapLibre are supported before trying to create the map
      try {
        if (typeof maplibregl.supported === 'function' && !maplibregl.supported()) {
          mapError.value = 'webgl_unsupported'
          return
        }
      } catch (e) {
        // If the feature check itself fails, treat as unsupported
        mapError.value = 'webgl_unsupported'
        return
      }

      const center = getGPSPosition(firstPointWithGps.data.gps_location)
      
      try {
        map.value = new maplibregl.Map({
          container: mapContainer.value,
          // Use OpenStreetMap raster tiles
          style: {
            version: 8,
            sources: {
              'osm': {
                type: 'raster',
                tiles: [
                  'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                  'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                  'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
                ],
                tileSize: 256,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              }
            },
            layers: [
              {
                id: 'osm',
                type: 'raster',
                source: 'osm',
                minzoom: 0,
                maxzoom: 19
              }
            ]
          },
          center: center, // Use the first point as the center
          zoom: 13,
          minZoom: 9,
          maxZoom: 18,
          attributionControl: false,
          bearing: 0,
          pitch: 0
        })
      } catch (e) {
        console.error('[OutdoorMap] Failed to initialize MapLibre map:', e)
        mapError.value = mapError.value || 'init_failed'
        return
      }

      // Wait for map style to load before adding sources and layers
      map.value.on('styledata', function() {
        if (map.value.isStyleLoaded()) {
          // Only set isMapInitialized and add sources/layers if style is fully loaded
          if (!isMapInitialized.value) {
            isMapInitialized.value = true

            // Add all sources and layers
            addMapSources()

            // Fit bounds to points if available
            fitMapToPoints()
          }
        }
      })

      // Listen for the load event as a fallback
      map.value.on('load', () => {
        // Check if style is loaded before proceeding
        if (map.value.isStyleLoaded() && !isMapInitialized.value) {
          isMapInitialized.value = true

          // Add all sources and layers
          addMapSources()

          // Fit bounds to points if available
          fitMapToPoints()
        }
      })

      // Generic map error handler (e.g. style/network issues)
      map.value.on('error', (event) => {
        console.error('[OutdoorMap] Map error event:', event && event.error)
        if (!isMapInitialized.value && !mapError.value) {
          mapError.value = 'init_failed'
        }
      })

      // Listen to zoom and pinch events to stop focusing on gps updates
      attachInteractionListeners()

    }

    // Add map sources and layers
    const addMapSources = () => {
      if (!map.value) return

      try {
        // Check if style is loaded before adding sources
        if (!map.value.isStyleLoaded()) {
          setTimeout(addMapSources, 100)
          return
        }

        // Add empty sources and layers for route
        if (!map.value.getSource('route')) {
          map.value.addSource('route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: []
              }
            }
          })

          map.value.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
              'visibility': 'none'
            },
            paint: {
              'line-color': '#4285F4',
              'line-width': 5,
              'line-opacity': 0.7
            }
          })
        }

        // Add layer for turn points
        if (!map.value.getSource('turns')) {
          map.value.addSource('turns', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            }
          })

          map.value.addLayer({
            id: 'turns',
            type: 'circle',
            source: 'turns',
            layout: {
              'visibility': 'none'
            },
            paint: {
              'circle-radius': 8,
              'circle-color': '#FF8C00',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#FFFFFF'
            }
          })
        }

        // Add layer for nearest point on route
        if (!map.value.getSource('nearest-point')) {
          map.value.addSource('nearest-point', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Point',
                coordinates: [0, 0]
              }
            }
          })

          map.value.addLayer({
            id: 'nearest-point',
            type: 'circle',
            source: 'nearest-point',
            layout: {
              'visibility': 'none'
            },
            paint: {
              'circle-radius': 6,
              'circle-color': '#00FF00',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#FFFFFF'
            }
          })
        }

        // Add active-turn source and layer
        if (!map.value.getSource('active-turn')) {
          map.value.addSource('active-turn', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Point',
                coordinates: [0, 0]
              }
            }
          })

          map.value.addLayer({
            id: 'active-turn',
            type: 'circle',
            source: 'active-turn',
            paint: {
              'circle-radius': 12,
              'circle-color': '#ff9500',
              'circle-opacity': 0.8,
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff'
            },
            layout: {
              'visibility': 'none'
            }
          })
        }

      } catch (error) {
        setTimeout(addMapSources, 500)
      }
    }

    // Fit map to points
    const fitMapToPoints = () => {
      if (!map.value || !props.points || Object.keys(props.points).length === 0) return

      // Check if style is loaded before proceeding
      if (!map.value.isStyleLoaded()) {
        setTimeout(fitMapToPoints, 100)
        return
      }

      try {
        // Create bounds object
        const bounds = new maplibregl.LngLatBounds()
        let hasValidBounds = false

        // Add all points to bounds
        Object.values(props.points).forEach(point => {
          if (point?.data?.gps_location && parseInt(point.data.status) === 0) {
            const gpsPosition = getGPSPosition(point.data.gps_location)
            if (!isNaN(gpsPosition.lat) && !isNaN(gpsPosition.lng)) {
              bounds.extend(gpsPosition)
              hasValidBounds = true
            }
          }
        })

        // Add current location to bounds if valid
        if (isValidLocation.value) {
          bounds.extend([props.currentLocation.lng, props.currentLocation.lat])
          hasValidBounds = true
        }

        // If we have valid bounds, fit the map to them
        if (hasValidBounds) {
          map.value.fitBounds(bounds, {
            padding: { top: 120, bottom: 150, left: 50, right: 50 },
            maxZoom: 16
          })
        }

      } catch (error) {
      }
    }

    // Update user location marker
    const updateUserMarker = () => {
      if (!map.value || !isMapInitialized.value) {
        return
      }
      
      // If location is valid, update user marker
      if (isValidLocation.value && props.currentLocation) {
        // Store the previous valid location
        if (!previousLocationRef.value) {
          previousLocationRef.value = { ...props.currentLocation }
        }
        
        // Create or update user location marker
        if (!userMarker.value) {
          // Create a DOM element for the marker
          const el = document.createElement('div')
          el.className = 'user-location-marker'
          el.style.width = '20px'
          el.style.height = '20px'
          el.style.borderRadius = '50%'
          el.style.backgroundColor = '#4285F4'
          el.style.border = '2px solid white'
          el.style.boxShadow = '0 0 5px rgba(0,0,0,0.3)'
          el.style.zIndex = '1000'
          
          // Create the marker
          userMarker.value = new maplibregl.Marker({
            element: el,
            rotationAlignment: 'map'
          })
            .setLngLat([props.currentLocation.lng, props.currentLocation.lat])
            .addTo(map.value)
          
        } else {
          // Update existing marker position with animation
          userMarker.value.setLngLat([props.currentLocation.lng, props.currentLocation.lat])
        }

        // Update previous location ref
        previousLocationRef.value = { ...props.currentLocation }
      }
    }

    // Add point markers
    const addPointMarkers = () => {
      if (!map.value || !isMapInitialized.value) return
      
      // Remove existing markers
      pointMarkers.value.forEach(marker => marker.remove())
      pointMarkers.value = []
      
      // Clear point states
      pointStates.value = {}
      
      // Add new markers for each point
      Object.entries(props.points).forEach(([tourPointId, point]) => {
        if (!point?.data?.gps_location) return
        
        const [lat, lng] = point.data.gps_location.split(',').map(parseFloat)
        if (isNaN(lat) || isNaN(lng)) return
        
        // Determine initial point state
        const pointId = point?.point?.id || point?.data?.point?.id || point?.id
        const isGPSLocked = props.isPointGPSLocked(pointId)
        pointStates.value[pointId] = isGPSLocked ? 'locked' : 'default'

        // Create marker element
        const el = document.createElement('div')
        el.className = 'point-marker'

        // Determine icon URL for the current state (custom or fallback)
        const currentState = getPointState(pointId)
        const customIconUrl = getCustomIconForState(point, currentState)
        const markerIconUrl = getMarkerIconUrl(point, currentState)

        el.style.backgroundImage = `url(${markerIconUrl})`
        el.style.width = '36px'
        el.style.height = '36px'
        el.style.backgroundSize = 'contain'

        // Add point number only when using fallback marker (no custom icon)
        if (!customIconUrl && point.data.order) {
          const numberEl = document.createElement('div')
          numberEl.className = 'point-number'
          numberEl.style.position = 'absolute'
          numberEl.style.top = '4px'
          numberEl.style.left = '50%'
          numberEl.style.transform = 'translateX(-50%)'
          numberEl.style.backgroundColor = 'white'
          numberEl.style.color = 'black'
          numberEl.style.borderRadius = '50%'
          numberEl.style.width = '20px'
          numberEl.style.height = '20px'
          numberEl.style.display = 'flex'
          numberEl.style.alignItems = 'center'
          numberEl.style.justifyContent = 'center'
          numberEl.style.fontSize = '14px'
          numberEl.style.fontWeight = 'bold'
          numberEl.textContent = point.data.order
          el.appendChild(numberEl)
        }
        
        // Create and add marker
        const marker = new maplibregl.Marker({
          element: el,
          anchor: 'bottom'
        })
          .setLngLat([lng, lat])
          .addTo(map.value)
        
        // Add click handler
        marker.getElement().addEventListener(
          'click', 
          () => {
            console.log('=== OUTDOOR MAP MARKER CLICK ===')
            console.log('Point clicked:', point)
            console.log('Point data:', point.data)
            
            // Use the parent's onItemClick handler for consistency
            if (props.onItemClick) {
              console.log('Calling parent onItemClick with point data')
              props.onItemClick(point.data, isOpenPreviewOnClick.value) // Pass boolean to show preview if true
            } else {
              console.warn('No onItemClick handler provided to OutdoorMap component')
            }
          })
        
        // Store marker reference for cleanup
        pointMarkers.value.push(marker)
      })
    }

    // Update point marker states based on current GPS position
    const updatePointMarkerStates = () => {
      if (!map.value || !isMapInitialized.value) return
      
      // Safety check: ensure points and pointMarkers exist
      if (!props.points || !pointMarkers.value || pointMarkers.value.length === 0) {
        console.warn('[OutdoorMap] Cannot update marker states: missing points or markers')
        return
      }

      // Iterate in the same order as markers were created in addPointMarkers
      Object.entries(props.points).forEach(([tourPointId, point], index) => {
        // Require a GPS location on the underlying point/tourpoint
        if (!point?.data?.point?.gps_location && !point?.data?.gps_location) return
        
        // Check if point state has changed
        const pointId = point?.point?.id || point?.data?.point?.id || point?.id
        const newState = props.isPointGPSLocked(pointId) ? 'locked' : 'default'
        const oldState = getPointState(pointId) // Use helper function

        console.log(`[OutdoorMap] Point ${pointId} oldState ${oldState}`)
        console.log(`[OutdoorMap] Point ${pointId} newState ${newState}`)
        
        if (newState !== oldState) {
          console.log(`[OutdoorMap] Point ${pointId} state changed from ${oldState} to ${newState}`)
          
          // Update stored state
          pointStates.value[pointId] = newState
          
          // Markers are created in the same order as Object.entries(props.points)
          const markerIndex = index
          
          if (markerIndex !== -1 && markerIndex < pointMarkers.value.length) {
            const marker = pointMarkers.value[markerIndex]
            if (!marker || !marker.getElement) {
              console.warn(`[OutdoorMap] Invalid marker found for point ${pointId}`)
              return
            }
            
            const markerElement = marker.getElement()

            // Update marker background based on new state (custom or fallback)
            const markerIconUrl = getMarkerIconUrl(point, newState)
            markerElement.style.backgroundImage = `url(${markerIconUrl})`
            
            console.log(`[OutdoorMap] Updated marker ${pointId} to state ${newState}`)
          } else {
            console.warn(`[OutdoorMap] Marker not found for point ${pointId} at index ${markerIndex}`)
          }
        }
      })
    }

    // Calculate direct route (as-the-crow-flies)
    const calculateDirectRoute = (start, end, pointData) => {
      if (!map.value) return
      
      // Create a simple LineString with just start and end points
      const directGeometry = {
        type: 'LineString',
        coordinates: [start, end]
      }
      
      // Store route data
      store.dispatch('navigation/setActiveRoute', {
        geometry: directGeometry,
        distance: distance(point(start), point(end), { units: 'meters' }),
        duration: null, // We don't know the duration for a direct route
        legs: [],
        destination: {
          coordinates: end,
          pointData: pointData
        }
      })
      
      // Set a single instruction
      store.dispatch('navigation/setRouteInstructions', [{
        text: `Loop rechstreeks naar het punt.`,
        distance: distance(point(start), point(end), { units: 'meters' }),
        type: "direct",
        modifier: "straight",
        location: end
      }])
      
      // Update route on map
      if (map.value.getSource('route')) {
        map.value.getSource('route').setData({
          type: 'Feature',
          properties: {},
          geometry: directGeometry
        })
        map.value.setLayoutProperty('route', 'visibility', 'visible')
      }
      
      // Hide turn points
      if (map.value.getSource('turns')) {
        map.value.getSource('turns').setData({
          type: 'FeatureCollection',
          features: []
        })
        map.value.setLayoutProperty('turns', 'visibility', 'none')
      }
      
      // Fit map to show route
      const routeBounds = new maplibregl.LngLatBounds()
        .extend(start)
        .extend(end)
      
      map.value.fitBounds(routeBounds, {
        padding: { top: 50, bottom: 150, left: 50, right: 50 },
        maxZoom: 16
      })
      
      // Initialize navigation
      updateNavigation()
    }

    // Calculate route between two points
    const calculateRoute = (start, end, pointData) => {
      if (!map.value || !isMapInitialized.value) return
      
      // Prevent multiple simultaneous route calculations
      if (calculatingRoute.value) {
        return
      }

      const now = Date.now()
      
      // Fetch route from OSRM - explicitly use walking profile
      const coordinates = `${start[0]},${start[1]};${end[0]},${end[1]}`
      
      api.getRoute('foot', coordinates, {
        overview: 'full',
        geometries: 'geojson',
        steps: 'true'
      })
        .then(response => {
          const data = response.data
          store.dispatch('navigation/setCalculatingRoute', false)
          lastRouteCalculationRef.value = now
          
          // Check if route calculation was successful
          if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
            store.dispatch('navigation/setRouteFailure', true)
            return
          }
          
          // Process route data
          processRouteData(data, start, end, pointData)
        })
        .catch(error => {
          store.dispatch('navigation/setCalculatingRoute', false)
          
          store.dispatch('navigation/setRouteFailure', true)
          // Fall back to direct route after a network error
          // calculateDirectRoute(start, end, pointData)
        })
    }

    // Helper function to process route data
    const processRouteData = (data, start, end, pointData) => {
      const route = data.routes[0]
      
      // Reset failure counter on success
      store.dispatch('navigation/clearRouteError')
      store.dispatch('navigation/setCalculatingRoute', false)
      const routeGeometry = route.geometry
      
      // Store route data
      store.dispatch('navigation/setActiveRoute', {
        geometry: routeGeometry,
        distance: route.distance,
        duration: route.duration,
        legs: route.legs,
        destination: {
          coordinates: end,
          pointData: pointData
        }
      })
      
      // Extract turn instructions
      const instructions = []
      route.legs.forEach(leg => {
        leg.steps.forEach(step => {
          instructions.push({
            text: step.maneuver.instruction || step.name,
            distance: step.distance,
            duration: step.duration,
            type: step.maneuver.type,
            modifier: step.maneuver.modifier,
            location: step.maneuver.location,
            bearing_before: step.maneuver.bearing_before,
            bearing_after: step.maneuver.bearing_after
          })
        })
      })

      store.dispatch('navigation/setRouteInstructions', instructions)
      
      // Update route on map
      if (map.value.getSource('route')) {
        map.value.getSource('route').setData({
          type: 'Feature',
          properties: {},
          geometry: routeGeometry
        })
        map.value.setLayoutProperty('route', 'visibility', 'visible')
      }
      
      // Add turn points to map
      const turnFeatures = instructions.map(instruction => ({
        type: 'Feature',
        properties: {
          description: instruction.text,
          type: instruction.type
        },
        geometry: {
          type: 'Point',
          coordinates: instruction.location
        }
      }))
      
      if (map.value.getSource('turns')) {
        map.value.getSource('turns').setData({
          type: 'FeatureCollection',
          features: turnFeatures
        })
        map.value.setLayoutProperty('turns', 'visibility', 'visible')
      }
      
      // Fit map to show route
      const routeBounds = new maplibregl.LngLatBounds()
      routeGeometry.coordinates.forEach(coord => {
        routeBounds.extend(coord)
      })
      
      map.value.fitBounds(routeBounds, {
        padding: { top: 50, bottom: 150, left: 50, right: 50 },
        maxZoom: 16
      })
      
      // Initialize navigation
      updateNavigation()
    }

    // Update navigation based on current location and route
    const updateNavigation = () => {
      if (!activeRoute.value || !isValidLocation.value || !map.value) return
      
      try {
        // Create points for current location and route
        const userPoint = point([props.currentLocation.lng, props.currentLocation.lat])
        const routeLine = lineString(activeRoute.value.geometry.coordinates)
        
        // Find nearest point on route
        const nearest = nearestPointOnLine(routeLine, userPoint)
        const nearestCoord = nearest.geometry.coordinates
        
        // Update nearest point marker
        if (map.value.getSource('nearest-point')) {
          map.value.getSource('nearest-point').setData({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: nearestCoord
            }
          })
          map.value.setLayoutProperty('nearest-point', 'visibility', 'visible')
        }
        
        // Calculate distance to nearest point (in meters)
        const distanceToRoute = nearest.properties.dist * 1000
        
        // Check if user is off route (more than 20 meters away)
        const offRouteThreshold = 20
        store.dispatch('navigation/setIsOffRoute', distanceToRoute > offRouteThreshold)
        
        if (distanceToRoute > offRouteThreshold) {
          // Recalculate route if significantly off route (more than 50 meters)
          // AND at least 10 seconds have passed since the last calculation
          // AND we're not already calculating a route
          if (distanceToRoute > 50 && 
              activeRoute.value.destination && 
              !calculatingRoute.value &&
              (Date.now() - lastRouteCalculationRef.value) > 10000) {
            
            calculateRoute(
              [props.currentLocation.lng, props.currentLocation.lat],
              activeRoute.value.destination.coordinates,
              activeRoute.value.destination.pointData
            )
          }
        }
        
        // Calculate direct distance to destination
        let directDistance = Infinity
        if (activeRoute.value && activeRoute.value.destination) {
          const destPoint = point(activeRoute.value.destination.coordinates)
          directDistance = distance(userPoint, destPoint, { units: 'meters' })
        }
        
        // Find next instruction
        const routeProgress = nearest.properties.location // Position along the route (0-1)
        const routeLength = activeRoute.value.distance // Total route length in meters
        const distanceTraveled = routeProgress * routeLength

        // Check if we've arrived at destination (within 10 meters)
        const hasArrived = directDistance <= 10

        if (hasArrived) {
          // Show arrival message
          store.dispatch('navigation/setNextInstruction', {
            text: "Je bent aangekomen bij je bestemming",
            distance: 0,
            type: "arrive"
          })
          store.dispatch('navigation/setDistanceToNextTurn', 0)
          
          // Stop navigation automatically after a short delay
          setTimeout(() => {
            store.dispatch('navigation/stopNavigation')
          }, 3000)
        } else {
          // Find all upcoming instructions with their actual distances
          const upcomingInstructions = []
          
          // Process each instruction
          if (routeInstructions.value && routeInstructions.value.length > 0) {
            for (let i = 0; i < routeInstructions.value.length; i++) {
              const instruction = routeInstructions.value[i]
              
              // Skip instructions without location
              if (!instruction.location) continue
              
              // Calculate actual distance from user to this instruction point
              const instructionPoint = point(instruction.location)
              const directDistanceToInstruction = distance(userPoint, instructionPoint, { units: 'meters' })
              
              // Calculate distance along route to this instruction
              // First find where this instruction is on the route
              const instructionOnRoute = nearestPointOnLine(routeLine, instructionPoint)
              const instructionRouteProgress = instructionOnRoute.properties.location
              
              // Only include instructions that are ahead of us on the route
              if (instructionRouteProgress > routeProgress) {
                // Calculate distance along the route (not direct)
                const distanceAlongRoute = (instructionRouteProgress - routeProgress) * routeLength
                
                upcomingInstructions.push({
                  index: i,
                  instruction: instruction,
                  directDistance: directDistanceToInstruction,
                  routeDistance: distanceAlongRoute,
                  routeProgress: instructionRouteProgress
                })
              }
            }
          }
          
          // Sort upcoming instructions by distance
          upcomingInstructions.sort((a, b) => a.distanceFromUser - b.distanceFromUser)
        
          // If we have upcoming instructions
          if (upcomingInstructions.length > 0) {
            // Get the next instruction (closest one along the route)
            const nextInstructionData = upcomingInstructions[0]
            const instruction = nextInstructionData.instruction
            const distanceToTurn = Math.max(1, Math.round(nextInstructionData.routeDistance)) // Ensure at least 1m
          
            // Check if we're very close to this instruction (within 10 meters)
            const isVeryCloseToTurn = distanceToTurn <= 10
            
            // If we're very close to a turn, also show the following instruction if available
            if (isVeryCloseToTurn && upcomingInstructions.length > 1) {
              const nextNextInstruction = upcomingInstructions[1].instruction
              const combinedText = `${instruction.text}, daarna ${nextNextInstruction.text}`
              
              store.dispatch('navigation/setNextInstruction', {
                ...instruction,
                text: combinedText
              })
            } else {
              store.dispatch('navigation/setNextInstruction', instruction)
            }
        
            // Force a state update by creating a new value
            const newDistanceToNextTurn = distanceToTurn
            store.dispatch('navigation/setDistanceToNextTurn', newDistanceToNextTurn)
            
            // If we're very close to a turn, highlight it on the map
            if (isVeryCloseToTurn && instruction.location) {
              if (map.value.getSource('active-turn')) {
                try {
                  map.value.getSource('active-turn').setData({
                    type: 'Feature',
                    properties: {
                      description: instruction.text
                    },
                    geometry: {
                      type: 'Point',
                      coordinates: instruction.location
                    }
                  })
                  map.value.setLayoutProperty('active-turn', 'visibility', 'visible')
                } catch (error) {
                }
              }
            } else {
              // Hide the active turn highlight when not close to a turn
              if (map.value.getSource('active-turn')) {
                map.value.setLayoutProperty('active-turn', 'visibility', 'none')
              }
            }
          } else {
            // No more instructions, but we haven't arrived yet
            // This can happen if we're between the last instruction and the destination
            store.dispatch('navigation/setNextInstruction', {
              text: "Ga door naar je bestemming",
              distance: directDistance,
              type: "continue"
            })
            store.dispatch('navigation/setDistanceToNextTurn', Math.round(directDistance))
          }
        }

      } catch (error) {
      }
    }

    // Update map view based on user location
    const updateMapView = () => {
      if (!map.value || !isMapInitialized.value || !isValidLocation.value) return

      // Check if heading is valid
      const isValidHeading = props.currentLocation.heading !== undefined && 
        props.currentLocation.heading !== null && 
        !isNaN(props.currentLocation.heading)

      // Check if speed is valid and above threshold
      const isMovingFastEnough = props.currentLocation.speed && 
          props.currentLocation.speed >= 0.9

      // Check if accuracy is good enough
      const hasGoodAccuracy = props.currentLocation.accuracy && 
        props.currentLocation.accuracy < 20
  
      // Update bearing smoothly if we have valid data and are moving fast enough
      if (isValidHeading && isMovingFastEnough && hasGoodAccuracy) {
        // Add to buffer for smoothing (keep last 5 valid bearings)
        bearingBufferRef.value.push(props.currentLocation.heading)
        if (bearingBufferRef.value.length > 5) {
          bearingBufferRef.value.shift()
        }
        
        // Calculate average bearing from buffer
        const sum = bearingBufferRef.value.reduce((acc, val) => acc + val, 0)
        const avg = sum / bearingBufferRef.value.length
        
        // Store as last valid bearing
        lastValidBearingRef.value = avg
        smoothedBearing.value = avg
      }
      
      // Determine bearing to use for map
      let bearing = 0
      let zoomLevel = 15
      
      if (activeRoute.value) {
        zoomLevel = 16
        // When navigating, always use the last valid bearing or current heading if moving
        if (isValidHeading && isMovingFastEnough && hasGoodAccuracy) {
          bearing = smoothedBearing.value
          
        } else if (lastValidBearingRef.value !== null) {
          // When stopped or slow, keep using the last valid bearing
          bearing = lastValidBearingRef.value
        }
      } else {
        // When not navigating, only use bearing when moving
        if (isValidHeading && isMovingFastEnough && hasGoodAccuracy) {
          bearing = smoothedBearing.value
          zoomLevel = 16
        } else if (lastValidBearingRef.value !== null) {
          // When stopped or slow, keep using the last valid bearing
          bearing = lastValidBearingRef.value
          zoomLevel = 16
        }
      }
      
      // Only follow the user automatically when enabled
      if (!isFollowingUser.value) {
        return
      }

      map.value.easeTo({
        center: [props.currentLocation.lng, props.currentLocation.lat],
        zoom: zoomLevel,
        bearing: bearing,
        duration: 500,
        pitch: 0
      })
    }

    // Clean up route display when activeRoute changes to null
    const clearRouteDisplay = () => {
      if (!activeRoute.value && map.value && isMapInitialized.value) {
        // Hide route layers
        try {
          map.value.setLayoutProperty('route', 'visibility', 'none')
          map.value.setLayoutProperty('turns', 'visibility', 'none')
          map.value.setLayoutProperty('active-turn', 'visibility', 'none')
          map.value.setLayoutProperty('nearest-point', 'visibility', 'none')
        } catch (error) {
        }
      }
    }

    // Watchers
    watch(() => props.currentLocation, (newLocation, oldLocation) => {
      checkLocationValidity()
      updateUserMarker()
      updateMapView()
      updatePointMarkerStates() // Call this watcher to update marker states
      
      // Update navigation when location changes
      if (activeRoute.value && isValidLocation.value) {
        updateNavigation()
      }
    }, { deep: true })

    watch(() => props.points, () => {
      console.log('=== OUTDOOR MAP POINTS CHANGED ===')
      console.log('New points:', props.points)
      console.log('Points count:', Object.keys(props.points || {}).length)
      console.log('Map initialized:', isMapInitialized.value)
      
      if (props.points && Object.keys(props.points).length > 0) {
        // Reset any previous error when we receive fresh points
        mapError.value = null
        if (!isMapInitialized.value) {
          // Map not initialized, initialize it
          console.log('Map not initialized, initializing...')
          initializeMap()
        } else {
          // Map already initialized, just update the markers
          console.log('Map already initialized, updating markers...')
          addPointMarkers()
          // Also fit map to new points
          fitMapToPoints()
        }
      } else {
        console.log('No points available')
        // Remove all markers if no points
        pointMarkers.value.forEach(marker => marker.remove())
        pointMarkers.value = []
        // Clear point states
        pointStates.value = {}
      }
    }, { deep: true })

    watch(() => isMapInitialized.value, () => {
      if (isMapInitialized.value) {
        addPointMarkers()
        // Also update user marker if location is available
        updateUserMarker()
      }
    })

    watch(() => activeRoute.value, () => {
      clearRouteDisplay()
    })

    // Lifecycle hooks
    onMounted(() => {
      // Initialize map when component is mounted
      if (props.points && Object.keys(props.points).length > 0) {
        initializeMap()
      }
      
      // Check location validity
      checkLocationValidity()
    })

    onUnmounted(() => {
      // Clean up map
      if (map.value) {
        detachInteractionListeners()
        map.value.remove()
        map.value = null
      }
      
      // Clean up markers
      pointMarkers.value.forEach(marker => marker.remove())
      pointMarkers.value = []

      clearFollowTimeout()
    })

    // Expose methods for parent components
    const calculateRouteToPoint = (pointData) => {
      if (isValidLocation.value && pointData?.gps_location) {
        const gpsPosition = getGPSPosition(pointData.gps_location)
        calculateRoute(
          [props.currentLocation.lng, props.currentLocation.lat],
          [gpsPosition.lng, gpsPosition.lat],
          pointData
        )
      }
    }

    // Expose method to manually update marker states (for external calls)
    const refreshMarkerStates = () => {
      updatePointMarkerStates()
    }

    return {
      mapContainer,
      isMapInitialized,
      calculateRouteToPoint,
      refreshMarkerStates,
      showFollowButton,
      enableFollowing,
      GpsIcon,
      mapError,
      mapErrorMessage
    }
  }
}
</script>
